import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { dashboardApi, categoryApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiBox,
  FiClock,
  FiShoppingCart,
  FiDollarSign,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiLayers,
  FiRefreshCw,
  FiUserCheck,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiShield,
  FiLock,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiTag,
  FiX,
  FiDownload,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

const formatDateDisplay = (dateStr) => {
  if (!dateStr || dateStr === "—") return "—";
  try {
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.slice(0, 10).split("-");
      return parts[2] + "-" + parts[1] + "-" + parts[0];
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return day + "-" + month + "-" + year;
  } catch (e) {
    return String(dateStr);
  }
};

const ReportsPage = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "stock-movement";

  // Tab State: 'stock-movement' | 'staff-activity' | 'item-history'
  const [activeTab, setActiveTab] = useState(
    initialTab === "user-audit" || initialTab === "staff-activity"
      ? "staff-activity"
      : initialTab === "item-history"
        ? "item-history"
        : "stock-movement"
  );

  // Subcategories metadata (Categories are hidden per user requirement)
  const [subcategories, setSubcategories] = useState([]);

  // --- TAB 1: Stock Movement Report (Purchases & Sales by Date & Time Range) ---
  const [movements, setMovements] = useState([]);
  const [movementSummary, setMovementSummary] = useState({
    total_movements: 0,
    total_purchases_qty: 0,
    total_sales_qty: 0,
    net_stock_movement: 0,
    total_purchases_amount: 0,
    total_sales_amount: 0,
  });
  const [movementLoading, setMovementLoading] = useState(false);

  // Date and Time Range States
  const [moveStartDate, setMoveStartDate] = useState("");
  const [moveEndDate, setMoveEndDate] = useState("");
  

  // Table Column Filters for Stock Movements
  const [filterMovementType, setFilterMovementType] = useState("all"); // 'all', 'purchase', 'sale'
  const [filterSubcategory, setFilterSubcategory] = useState("all");
  const [filterParty, setFilterParty] = useState("all");
  const [filterLoggedBy, setFilterLoggedBy] = useState("all");
  const [filterItemText, setFilterItemText] = useState("");
  const [filterSerialText, setFilterSerialText] = useState("");

  // Pagination for Stock Movement
  const [movePage, setMovePage] = useState(1);
  const [movePageSize, setMovePageSize] = useState(10);

  // Interactive Calendar State
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectingRangeEnd, setSelectingRangeEnd] = useState(false);
  const [allActivityDates, setAllActivityDates] = useState(new Set());

  // --- TAB 2: Staff Activity & Work Audit ---
  const [userAuditData, setUserAuditData] = useState({ users: [], transactions: [], summary: {} });
  const [userAuditLoading, setUserAuditLoading] = useState(false);
  const [auditUserFilter, setAuditUserFilter] = useState("all");
  const [auditTypeFilter, setAuditTypeFilter] = useState("all");
  const [auditSearch, setAuditSearch] = useState("");
  const [auditStartDate, setAuditStartDate] = useState("");
  const [auditEndDate, setAuditEndDate] = useState("");

  // Pagination for Staff Activity
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);

  // --- TAB 3: Item Full Detail & Lifecycle History ---
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [itemHistoryResults, setItemHistoryResults] = useState([]);
  const [itemHistoryLoading, setItemHistoryLoading] = useState(false);

  // 1. Load Subcategories & Global Activity Dates on Mount
  useEffect(() => {
    categoryApi
      .getSubcategories()
      .then((res) => setSubcategories(res.data || []))
      .catch((err) => console.error("Failed to load subcategories:", err));

    // Fetch initial list of all transaction dates to render dots on the calendar
    dashboardApi
      .getStockMovementReport({})
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.movements)
            ? res.data.movements
            : Array.isArray(res.movements)
              ? res.movements
              : [];
        const dateSet = new Set(list.map((m) => m.transaction_date).filter(Boolean));
        setAllActivityDates(dateSet);
      })
      .catch(() => { });
  }, []);

  // 2. Fetch Stock Movement Report (Purchases & Sales for Selected Date Range)
  const fetchMovementReport = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setMovementLoading(true);
      const params = {};
      if (moveStartDate) params.start_date = moveStartDate;
      if (moveEndDate) params.end_date = moveEndDate;

      const res = await dashboardApi.getStockMovementReport(params);
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.movements)
          ? res.data.movements
          : Array.isArray(res.movements)
            ? res.movements
            : [];
      setMovements(list);
      const summaryData = res.summary || res.data?.summary;
      if (summaryData) setMovementSummary(summaryData);
    } catch (err) {
      toast.error(err.message || "Failed to load stock movement report");
    } finally {
      setMovementLoading(false);
    }
  }, [isAdmin, moveStartDate, moveEndDate]);

  useEffect(() => {
    if (activeTab === "stock-movement") {
      const timer = setTimeout(() => {
        fetchMovementReport();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeTab, fetchMovementReport]);

  // Reset page when movement filters change
  useEffect(() => {
    setMovePage(1);
  }, [
    moveStartDate,
    moveEndDate,
    filterMovementType,
    filterSubcategory,
    filterParty,
    filterLoggedBy,
    filterItemText,
    filterSerialText,
    movePageSize,
  ]);

  // 3. Fetch Staff Activity Report
  const fetchUserAuditReport = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setUserAuditLoading(true);
      const params = {};
      if (auditUserFilter !== "all") params.user_id = auditUserFilter;
      if (auditTypeFilter !== "all") params.record_type = auditTypeFilter;
      if (auditSearch.trim()) params.search = auditSearch.trim();
      if (auditStartDate) params.start_date = auditStartDate;
      if (auditEndDate) params.end_date = auditEndDate;

      const res = await dashboardApi.getUserAuditReport(params);
      setUserAuditData(res.data || { users: [], transactions: [], summary: {} });
    } catch (err) {
      toast.error(err.message || "Failed to load staff activity audit report");
    } finally {
      setUserAuditLoading(false);
    }
  }, [isAdmin, auditUserFilter, auditTypeFilter, auditSearch, auditStartDate, auditEndDate]);

  useEffect(() => {
    if (activeTab === "staff-activity") {
      const timer = setTimeout(() => {
        fetchUserAuditReport();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeTab, fetchUserAuditReport]);

  // Reset page when audit filters change
  useEffect(() => {
    setAuditPage(1);
  }, [auditUserFilter, auditTypeFilter, auditSearch, auditStartDate, auditEndDate, auditPageSize]);

  // 4. Fetch Item Lifecycle History (Search full details: when purchased & from which vendor, when sold & to which client)
  const fetchItemHistory = useCallback(async (searchTerm) => {
    if (!isAdmin || !searchTerm || !searchTerm.trim()) {
      setItemHistoryResults([]);
      return;
    }
    try {
      setItemHistoryLoading(true);
      const res = await dashboardApi.getItemHistory({ search: searchTerm.trim() });
      setItemHistoryResults(res.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load item lifecycle detail");
    } finally {
      setItemHistoryLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === "item-history") {
      const timer = setTimeout(() => {
        if (itemSearchTerm.trim()) {
          fetchItemHistory(itemSearchTerm);
        } else {
          setItemHistoryResults([]);
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [activeTab, itemSearchTerm, fetchItemHistory]);

  // Helper to jump to Item Lifecycle tab from table row
  const inspectItemLifecycle = (identifier) => {
    setActiveTab("item-history");
    setItemSearchTerm(identifier);
    fetchItemHistory(identifier);
  };

  // Quick Date Range Presets
  const applyQuickDate = (preset) => {
    const today = new Date();
    const formatDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    if (preset === "today") {
      const str = formatDate(today);
      setMoveStartDate(str);
      setMoveEndDate(str);
      setCalendarDate(new Date(today));
    } else if (preset === "yesterday") {
      const yest = new Date();
      yest.setDate(today.getDate() - 1);
      const str = formatDate(yest);
      setMoveStartDate(str);
      setMoveEndDate(str);
      setCalendarDate(new Date(yest));
    } else if (preset === "last7") {
      const past = new Date();
      past.setDate(today.getDate() - 6);
      setMoveStartDate(formatDate(past));
      setMoveEndDate(formatDate(today));
    } else if (preset === "thisMonth") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setMoveStartDate(formatDate(firstDay));
      setMoveEndDate(formatDate(today));
      setCalendarDate(new Date(today));
    } else if (preset === "all") {
      setMoveStartDate("");
      setMoveEndDate("");
    }
    setSelectingRangeEnd(false);
  };

  

  // Calendar Helpers
  const calendarYear = calendarDate.getFullYear();
  const calendarMonth = calendarDate.getMonth();

  const prevMonth = () => {
    setCalendarDate(new Date(calendarYear, calendarMonth - 1, 1));
  };

  const nextMonth = () => {
    setCalendarDate(new Date(calendarYear, calendarMonth + 1, 1));
  };

  const currentMonthName = calendarDate.toLocaleString("default", { month: "long" });
  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const firstDayWeekday = new Date(calendarYear, calendarMonth, 1).getDay();

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const monthStr = String(calendarMonth + 1).padStart(2, "0");
      const dayStr = String(d).padStart(2, "0");
      const fullDate = `${calendarYear}-${monthStr}-${dayStr}`;
      days.push({
        dayNumber: d,
        dateStr: fullDate,
        hasActivity: allActivityDates.has(fullDate),
      });
    }
    return days;
  }, [calendarYear, calendarMonth, daysInMonth, firstDayWeekday, allActivityDates]);

  // Calendar Day Click Handler: Click once to set date, or click again to set range
  const handleCalendarDayClick = (dateStr) => {
    if (!selectingRangeEnd || !moveStartDate) {
      setMoveStartDate(dateStr);
      setMoveEndDate(dateStr);
      setSelectingRangeEnd(true);
    } else {
      if (dateStr < moveStartDate) {
        setMoveEndDate(moveStartDate);
        setMoveStartDate(dateStr);
      } else {
        setMoveEndDate(dateStr);
      }
      setSelectingRangeEnd(false);
    }
  };

  const isDateSelected = (dateStr) => {
    if (!dateStr) return false;
    if (moveStartDate && moveEndDate) {
      return dateStr >= moveStartDate && dateStr <= moveEndDate;
    }
    return dateStr === moveStartDate;
  };

  const isDateRangeEdge = (dateStr) => {
    return dateStr === moveStartDate || dateStr === moveEndDate;
  };

  const selectedPeriodLabel = useMemo(() => {
    if (!moveStartDate && !moveEndDate) {
      return "All Recorded Dates";
    }
    if (moveStartDate === moveEndDate) {
      return formatDateDisplay(moveStartDate);
    }
    const startFormatted = moveStartDate ? formatDateDisplay(moveStartDate) : "Start";
    const endFormatted = moveEndDate ? formatDateDisplay(moveEndDate) : "End";
    return `${startFormatted} to ${endFormatted}`;
  }, [moveStartDate, moveEndDate]);

  // Dynamic options for Sub-category filter
  const allSubcategoriesList = useMemo(() => {
    const set = new Set();
    (subcategories || []).forEach((s) => {
      if (s.name && s.name.trim()) set.add(s.name.trim());
    });
    (movements || []).forEach((m) => {
      if (m.subcategory_name && m.subcategory_name.trim()) set.add(m.subcategory_name.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [subcategories, movements]);

  // Dynamic options for Party (Vendor / Client) filter
  const distinctParties = useMemo(() => {
    const set = new Set();
    (movements || []).forEach((m) => {
      if (m.party_name && m.party_name.trim()) set.add(m.party_name.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [movements]);

  // Dynamic options for Logged By filter
  const distinctLoggedBy = useMemo(() => {
    const map = new Map();
    (movements || []).forEach((m) => {
      const name = m.created_by_name || (m.created_by_username ? `@${m.created_by_username}` : "System");
      if (name) {
        map.set(name, m.created_by_username && m.created_by_name ? `${m.created_by_name} (@${m.created_by_username})` : name);
      }
    });
    return Array.from(map.entries()).map(([name, label]) => ({ name, label }));
  }, [movements]);

  // Filter movements according to table column filters & time range
  const filteredMovements = useMemo(() => {
    return (movements || []).filter((m) => {
      // 1. Movement Type
      if (filterMovementType !== "all") {
        if (m.record_type !== filterMovementType) return false;
      }

      // 2. Sub-category
      if (filterSubcategory !== "all") {
        const subName = (m.subcategory_name || "").toLowerCase();
        if (subName !== filterSubcategory.toLowerCase() && String(m.sub_category_id) !== String(filterSubcategory)) {
          return false;
        }
      }

      // 3. Party (Vendor / Client)
      if (filterParty !== "all") {
        if ((m.party_name || "").trim().toLowerCase() !== filterParty.trim().toLowerCase()) {
          return false;
        }
      }

      // 4. Logged By Staff
      if (filterLoggedBy !== "all") {
        const staffName = m.created_by_name || (m.created_by_username ? `@${m.created_by_username}` : "System");
        if (staffName !== filterLoggedBy) return false;
      }

      // 5. Item & SKU text filter
      if (filterItemText.trim()) {
        const q = filterItemText.toLowerCase();
        const matchItem =
          (m.item_name && m.item_name.toLowerCase().includes(q)) ||
          (m.sku && m.sku.toLowerCase().includes(q)) ||
          (m.bill_number && m.bill_number.toLowerCase().includes(q)) ||
          (m.invoice_number && m.invoice_number.toLowerCase().includes(q));
        if (!matchItem) return false;
      }

      // 6. Serial & Part Number text filter
      if (filterSerialText.trim()) {
        const q = filterSerialText.toLowerCase();
        const matchSerial =
          (m.serial_number && m.serial_number.toLowerCase().includes(q)) ||
          (m.part_number && m.part_number.toLowerCase().includes(q));
        if (!matchSerial) return false;
      }

      return true;
    });
  }, [
    movements,
    filterMovementType,
    filterSubcategory,
    filterParty,
    filterLoggedBy,
    filterItemText,
    filterSerialText,
    ]);

  // Active filters checker & reset function
  const hasActiveFilters =
    filterMovementType !== "all" ||
    filterSubcategory !== "all" ||
    filterParty !== "all" ||
    filterLoggedBy !== "all" ||
    filterItemText.trim() !== "" ||
    filterSerialText.trim() !== "";

  const resetAllTableFilters = () => {
    setFilterMovementType("all");
    setFilterSubcategory("all");
    setFilterParty("all");
    setFilterLoggedBy("all");
    setFilterItemText("");
    setFilterSerialText("");
  };

  // Dynamic KPI summary reflecting filtered results
  const filteredSummary = useMemo(() => {
    const total_movements = filteredMovements.length;
    let purchases_qty = 0;
    let sales_qty = 0;
    let purchases_amount = 0;
    let sales_amount = 0;

    filteredMovements.forEach((m) => {
      const qty = parseInt(m.quantity) || 1;
      const price = parseFloat(m.total_price || m.unit_price || 0);
      if (m.record_type === "purchase") {
        purchases_qty += qty;
        purchases_amount += price;
      } else if (m.record_type === "sale") {
        sales_qty += qty;
        sales_amount += price;
      }
    });

    return {
      total_movements,
      total_purchases_qty: purchases_qty,
      total_sales_qty: sales_qty,
      net_stock_movement: purchases_qty - sales_qty,
      total_purchases_amount: purchases_amount,
      total_sales_amount: sales_amount,
    };
  }, [filteredMovements]);

  // Excel Download Function (reflecting whatever filters are applied)
  const downloadMovementsExcel = () => {
    if (!filteredMovements || filteredMovements.length === 0) {
      toast.warning("No data found matching your selected filters to download.");
      return;
    }

    try {
      const metaRows = [
        ["Commercial IT - Purchases & Sales Movements Report"],
        ["Date Range Selected:", selectedPeriodLabel],
        ["Generated On:", new Date().toLocaleString()],
        [
          "Filters Applied:",
          `Movement Type: ${filterMovementType === "all" ? "All Movements" : filterMovementType === "purchase" ? "Stock IN (Purchase)" : "Stock OUT (Sale)"} | Sub-category: ${filterSubcategory} | Party: ${filterParty} | Logged By: ${filterLoggedBy}`,
        ],
        ["Total Filtered Movements:", filteredMovements.length],
        [], // empty spacer row
      ];

      const headers = [
        "#",
        "Date",
        "Movement Type",
        "Item Name",
        "SKU",
        "Serial Number",
        "Part Number",
        "Sub-category",
        "Party (Vendor / Client)",
        "Party Type",
        "Party Contact",
        "Logged By Staff",
        "Staff Username",
        "Ref # (Bill / Invoice)",
        "Quantity",
        "Unit Price",
        "Total Price",
      ];

      const rows = filteredMovements.map((m, idx) => {
        const isPurchase = m.record_type === "purchase";
        const dateObj = m.created_at ? new Date(m.created_at) : null;

        return [
          idx + 1,
          formatDateDisplay(m.transaction_date),
          isPurchase ? "Stock IN (Purchase)" : "Stock OUT (Sale)",
          m.item_name || "—",
          m.sku || "—",
          m.serial_number || "—",
          m.part_number || "—",
          m.subcategory_name || "—",
          m.party_name || "—",
          isPurchase ? "Vendor / Supplier" : "Client / Buyer",
          m.party_contact || "—",
          m.created_by_name || "System",
          m.created_by_username ? `@${m.created_by_username}` : "—",
          m.bill_number || m.invoice_number || "—",
          m.quantity || 1,
          m.unit_price || 0,
          m.total_price || m.unit_price || 0,
        ];
      });

      const worksheetData = [...metaRows, headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);

      ws["!cols"] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 14 },
        { wch: 22 },
        { wch: 28 },
        { wch: 16 },
        { wch: 18 },
        { wch: 16 },
        { wch: 18 },
        { wch: 26 },
        { wch: 18 },
        { wch: 16 },
        { wch: 20 },
        { wch: 16 },
        { wch: 22 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stock Movements");

      const safeStart = (moveStartDate || "all").replace(/[^a-zA-Z0-9_-]/g, "");
      const safeEnd = (moveEndDate || "all").replace(/[^a-zA-Z0-9_-]/g, "");
      const fileName = `Stock_Movement_Report_${safeStart}_to_${safeEnd}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success("Excel report sheet downloaded successfully!");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to generate Excel report sheet");
    }
  };

  // Pagination calculations for Stock Movements (using filteredMovements)
  const safeMovements = Array.isArray(filteredMovements) ? filteredMovements : [];
  const totalMovePages = Math.max(1, Math.ceil(safeMovements.length / movePageSize));
  const moveStartIdx = (movePage - 1) * movePageSize;
  const moveEndIdx = Math.min(moveStartIdx + movePageSize, safeMovements.length);
  const paginatedMovements = useMemo(() => {
    return safeMovements.slice(moveStartIdx, moveEndIdx);
  }, [safeMovements, moveStartIdx, moveEndIdx]);

  // Pagination calculations for Staff Activity
  const auditTransactionsList = userAuditData.transactions || [];
  const totalAuditPages = Math.max(1, Math.ceil(auditTransactionsList.length / auditPageSize));
  const auditStartIdx = (auditPage - 1) * auditPageSize;
  const auditEndIdx = Math.min(auditStartIdx + auditPageSize, auditTransactionsList.length);
  const paginatedAuditTransactions = useMemo(() => {
    return auditTransactionsList.slice(auditStartIdx, auditEndIdx);
  }, [auditTransactionsList, auditStartIdx, auditEndIdx]);

  // RBAC Access Control Check: Reports should only be shown to Admin
  if (!isAdmin) {
    return (
      <div className="fade-in" style={{ padding: "40px 20px", textAlign: "center" }}>
        <div
          style={{
            maxWidth: 500,
            margin: "40px auto",
            backgroundColor: "#fff",
            borderRadius: "var(--radius-lg)",
            padding: "36px 28px",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FiLock size={28} />
          </div>
          <h2 style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--navy)", marginBottom: 8 }}>
            Admin Access Required
          </h2>
          <p style={{ fontSize: "0.88rem", color: "var(--slate-500)", marginBottom: 24, lineHeight: 1.5 }}>
            Reports and movement audit intelligence are confidential and restricted to Administrator accounts only.
          </p>
          <Link
            to="/stock"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: "var(--primary)",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "var(--radius-md)",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "0.88rem",
            }}
          >
            <FiBox size={16} /> Go to Stock Overview
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Page Header (Print button removed per user requirement) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)" }}>
              Admin Reports & Audits
            </h2>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                backgroundColor: "var(--accent-light, #EDE9FE)",
                color: "var(--accent, #7C3AED)",
                padding: "3px 10px",
                borderRadius: 12,
                fontSize: "0.75rem",
                fontWeight: 800,
              }}
            >
              <FiShield size={12} /> Admin Only
            </span>
          </div>

        </div>
      </div>

      {/* Admin Report Navigation Tabs */}
      <div
        style={{
          display: "flex",
          gap: 8,
          borderBottom: "2px solid var(--slate-200)",
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        {/* Tab 1: Stock Movements with Date Range */}
        <button
          onClick={() => setActiveTab("stock-movement")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 18px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "0.92rem",
            fontWeight: activeTab === "stock-movement" ? 800 : 600,
            color: activeTab === "stock-movement" ? "var(--primary)" : "var(--slate-500)",
            borderBottom: activeTab === "stock-movement" ? "3px solid var(--primary)" : "3px solid transparent",
            marginBottom: -2,
            transition: "all 0.15s",
          }}
        >
          <FiCalendar size={17} />
          Purchases & Sales Movements
          <span
            style={{
              padding: "2px 7px",
              borderRadius: 10,
              fontSize: "0.72rem",
              backgroundColor: activeTab === "stock-movement" ? "var(--primary-light)" : "var(--slate-100)",
              color: activeTab === "stock-movement" ? "var(--primary)" : "var(--slate-600)",
            }}
          >
            {movements.length}
          </span>
        </button>

        {/* Tab 2: Staff Activity */}
        <button
          onClick={() => setActiveTab("staff-activity")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 18px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "0.92rem",
            fontWeight: activeTab === "staff-activity" ? 800 : 600,
            color: activeTab === "staff-activity" ? "var(--accent, #7C3AED)" : "var(--slate-500)",
            borderBottom: activeTab === "staff-activity" ? "3px solid var(--accent, #7C3AED)" : "3px solid transparent",
            marginBottom: -2,
            transition: "all 0.15s",
          }}
        >
          <FiUserCheck size={17} />
          Staff Activity & Work Audit
          <span
            style={{
              padding: "2px 7px",
              borderRadius: 10,
              fontSize: "0.72rem",
              backgroundColor: activeTab === "staff-activity" ? "var(--accent-light, #EDE9FE)" : "var(--slate-100)",
              color: activeTab === "staff-activity" ? "var(--accent, #7C3AED)" : "var(--slate-600)",
            }}
          >
            {userAuditData.transactions?.length || 0}
          </span>
        </button>

        {/* Tab 3: Item Full Detail Lifecycle History */}
        <button
          onClick={() => setActiveTab("item-history")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 18px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "0.92rem",
            fontWeight: activeTab === "item-history" ? 800 : 600,
            color: activeTab === "item-history" ? "var(--navy)" : "var(--slate-500)",
            borderBottom: activeTab === "item-history" ? "3px solid var(--navy)" : "3px solid transparent",
            marginBottom: -2,
            transition: "all 0.15s",
          }}
        >
          <FiSearch size={17} />
          Item Lifecycle & History
          <span
            style={{
              padding: "2px 7px",
              borderRadius: 10,
              fontSize: "0.72rem",
              backgroundColor: activeTab === "item-history" ? "var(--slate-200)" : "var(--slate-100)",
              color: "var(--navy)",
              fontWeight: 700,
            }}
          >
            Lookup
          </span>
        </button>
      </div>

      {/* ======================================================== */}
      {/* TAB 1: DATE RANGE PURCHASES & SALES MOVEMENTS            */}
      {/* ======================================================== */}
      {activeTab === "stock-movement" && (
        <div>
          {/* Top Section: Interactive Calendar + Direct Date Pickers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 20,
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "22px 24px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 20,
            }}
          >
            {/* Visual Calendar Component */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FiCalendar size={18} color="var(--primary)" />
                  <span style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)" }}>
                    {currentMonthName} {calendarYear}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    type="button"
                    onClick={prevMonth}
                    title="Previous Month"
                    style={{
                      padding: "6px 8px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-200)",
                      backgroundColor: "var(--slate-50)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCalendarDate(new Date())}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-200)",
                      backgroundColor: "var(--slate-50)",
                      cursor: "pointer",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                    }}
                  >
                    Current
                  </button>
                  <button
                    type="button"
                    onClick={nextMonth}
                    title="Next Month"
                    style={{
                      padding: "6px 8px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-200)",
                      backgroundColor: "var(--slate-50)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 4,
                  textAlign: "center",
                }}
              >
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div
                    key={day}
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "var(--slate-400)",
                      paddingBottom: 6,
                    }}
                  >
                    {day}
                  </div>
                ))}

                {calendarDays.map((d, index) => {
                  if (!d) {
                    return <div key={`empty-${index}`} style={{ height: 38 }} />;
                  }

                  const selected = isDateSelected(d.dateStr);
                  const isEdge = isDateRangeEdge(d.dateStr);

                  return (
                    <button
                      key={d.dateStr}
                      type="button"
                      onClick={() => handleCalendarDayClick(d.dateStr)}
                      style={{
                        height: 38,
                        borderRadius: isEdge ? 8 : selected ? 4 : 8,
                        border: isEdge ? "2px solid var(--primary)" : "1px solid transparent",
                        backgroundColor: isEdge
                          ? "var(--primary)"
                          : selected
                            ? "var(--primary-light, #EEF2FF)"
                            : "transparent",
                        color: isEdge ? "#fff" : selected ? "var(--primary)" : "var(--slate-700)",
                        fontWeight: selected ? 800 : 600,
                        fontSize: "0.84rem",
                        cursor: "pointer",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) e.currentTarget.style.backgroundColor = "var(--slate-100)";
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <span>{d.dayNumber}</span>
                      {d.hasActivity && (
                        <span
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            backgroundColor: isEdge ? "#fff" : "var(--accent, #7C3AED)",
                            marginTop: 1,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Quick Date Presets Chips */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
                <span style={{ fontSize: "0.76rem", fontWeight: 700, color: "var(--slate-400)", alignSelf: "center" }}>
                  Quick Presets:
                </span>
                {["today", "yesterday", "last7", "thisMonth", "all"].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyQuickDate(preset)}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 14,
                      border: "1px solid var(--slate-200)",
                      backgroundColor: "var(--slate-50)",
                      fontSize: "0.74rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {preset === "last7" ? "Last 7 Days" : preset === "thisMonth" ? "This Month" : preset === "all" ? "All Time" : preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Inputs & Status Panel */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                paddingLeft: 12,
                borderLeft: "1px solid var(--slate-100)",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <FiCalendar size={18} color="var(--primary)" />
                  <span style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)" }}>
                    Date Range Selection
                  </span>
                </div>

                {/* Direct Date Range Pickers */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "var(--slate-600)", marginBottom: 4 }}>
                      Start Date:
                    </label>
                    <input
                      type="date"
                      value={moveStartDate}
                      onChange={(e) => setMoveStartDate(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.84rem",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "var(--slate-600)", marginBottom: 4 }}>
                      End Date:
                    </label>
                    <input
                      type="date"
                      value={moveEndDate}
                      onChange={(e) => setMoveEndDate(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.84rem",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Active Selection Feedback Box */}
              <div
                style={{
                  backgroundColor: "var(--slate-50)",
                  borderRadius: "var(--radius-md)",
                  padding: "12px 14px",
                  border: "1px solid var(--slate-200)",
                }}
              >
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--slate-500)", textTransform: "uppercase" }}>
                  Selected Period
                </div>
                <div style={{ fontSize: "0.98rem", fontWeight: 800, color: "var(--navy)", marginTop: 2 }}>
                  {selectedPeriodLabel}
                </div>
                
              </div>
            </div>
          </div>

          {/* Movement Summary KPI Cards for Selected Date / Period */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
              gap: 16,
              marginBottom: 20,
            }}
          >
            {/* Total Movements in Selected Period */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                padding: "18px 20px",
                border: "1px solid var(--slate-200)",
                boxShadow: "var(--shadow-sm)",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  backgroundColor: "var(--slate-100)",
                  color: "var(--navy)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiCalendar size={22} />
              </div>
              <div>
                <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
                  Period Movements
                </div>
                <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "var(--navy)", marginTop: 2 }}>
                  {filteredSummary.total_movements}{" "}
                  <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-400)" }}>trans.</span>
                </div>
              </div>
            </div>

            {/* Total Purchases in Selected Period */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                padding: "18px 20px",
                border: "1px solid var(--slate-200)",
                boxShadow: "var(--shadow-sm)",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  backgroundColor: "#E0F2FE",
                  color: "#0369A1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiArrowDownLeft size={22} />
              </div>
              <div>
                <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
                  Purchases (IN)
                </div>
                <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#0369A1", marginTop: 2 }}>
                  {filteredSummary.total_purchases_qty}{" "}
                  <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-400)" }}>units</span>
                </div>

              </div>
            </div>

            {/* Total Sales in Selected Period */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                padding: "18px 20px",
                border: "1px solid var(--slate-200)",
                boxShadow: "var(--shadow-sm)",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  backgroundColor: "var(--success-bg)",
                  color: "var(--success)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiArrowUpRight size={22} />
              </div>
              <div>
                <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
                  Sales (OUT)
                </div>
                <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "var(--success)", marginTop: 2 }}>
                  {filteredSummary.total_sales_qty}{" "}
                  <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-400)" }}>units</span>
                </div>

              </div>
            </div>

            {/* Net Movement in Selected Period */}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                padding: "18px 20px",
                border: "1px solid var(--slate-200)",
                boxShadow: "var(--shadow-sm)",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  backgroundColor: "var(--slate-100)",
                  color: "var(--navy)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiBox size={22} />
              </div>
              <div>
                <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
                  Net Stock Shift
                </div>
                <div
                  style={{
                    fontSize: "1.55rem",
                    fontWeight: 800,
                    color: filteredSummary.net_stock_movement >= 0 ? "var(--primary)" : "var(--danger)",
                    marginTop: 2,
                  }}
                >
                  {filteredSummary.net_stock_movement > 0 ? `+${filteredSummary.net_stock_movement}` : filteredSummary.net_stock_movement}{" "}
                  <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--slate-400)" }}>units</span>
                </div>
              </div>
            </div>
          </div>

          {/* Movement Details Table: Subcategory shown, Category NOT shown */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            {/* Table Header Action Bar with Date & Time Range, Excel Download & Refresh */}
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid var(--slate-200)",
                backgroundColor: "var(--slate-50)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.98rem", fontWeight: 800, color: "var(--navy)" }}>
                  Purchases & Sales Movements
                </span>

                {/* Selected Date & Time display */}
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    backgroundColor: "#EEF2FF",
                    color: "var(--primary)",
                    padding: "3px 10px",
                    borderRadius: 14,
                    fontSize: "0.76rem",
                    fontWeight: 700,
                  }}
                >
                  <FiCalendar size={13} />
                  <span>Date: {selectedPeriodLabel}</span>
                </span>

                

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: "var(--slate-200)",
                    color: "var(--slate-700)",
                    padding: "3px 8px",
                    borderRadius: 12,
                    fontSize: "0.74rem",
                    fontWeight: 700,
                  }}
                >
                  {filteredMovements.length} {filteredMovements.length === 1 ? "record" : "records"}
                  {filteredMovements.length !== movements.length && ` (of ${movements.length})`}
                </span>
              </div>

              {/* Right: Download Sheet (.xlsx) and Refresh Buttons */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  onClick={downloadMovementsExcel}
                  disabled={filteredMovements.length === 0}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: "var(--radius-md)",
                    backgroundColor: filteredMovements.length === 0 ? "var(--slate-300)" : "var(--primary)",
                    color: "#fff",
                    border: "none",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    cursor: filteredMovements.length === 0 ? "not-allowed" : "pointer",
                    boxShadow: filteredMovements.length === 0 ? "none" : "0 2px 6px rgba(6, 78, 59, 0.2)",
                    transition: "all 0.15s ease",
                  }}
                  title="Download the current filtered report table as Excel (.xlsx)"
                >
                  <FiDownload size={15} /> Download Sheet (.xlsx)
                </button>

                <button
                  type="button"
                  onClick={fetchMovementReport}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 12px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-600)",
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  <FiRefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" }}>
                <thead>
                  {/* Row 1: Column Headings */}
                  <tr style={{ backgroundColor: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 140 }}>Date</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 155 }}>Movement Type</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 170 }}>Item & SKU</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 140 }}>Serial / Part #</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 160 }}>Sub-category</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 180 }}>Party (Vendor / Client)</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, minWidth: 155 }}>Logged By</th>
                    <th style={{ padding: "12px 14px", color: "var(--slate-700)", fontWeight: 700, textAlign: "right", minWidth: 85 }}>Action</th>
                  </tr>

                  {/* Row 2: In-Table Filters */}
                  <tr style={{ backgroundColor: "#F8FAFC", borderBottom: "2px solid var(--slate-200)" }}>
                    {/* Date & Time display */}
                    <th style={{ padding: "6px 14px" }}>
                      <span style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 600 }}>
                        {selectedPeriodLabel}
                      </span>
                    </th>

                    {/* Filter 1: Movement Type */}
                    <th style={{ padding: "6px 14px" }}>
                      <select
                        value={filterMovementType}
                        onChange={(e) => setFilterMovementType(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: filterMovementType !== "all" ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                          backgroundColor: filterMovementType !== "all" ? "#F0FDF4" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "var(--slate-700)",
                        }}
                      >
                        <option value="all">All Movements</option>
                        <option value="purchase">Stock IN (Purchase)</option>
                        <option value="sale">Stock OUT (Sale)</option>
                      </select>
                    </th>

                    {/* Filter 2: Item & SKU */}
                    <th style={{ padding: "6px 14px" }}>
                      <input
                        type="text"
                        placeholder="Filter item or SKU..."
                        value={filterItemText}
                        onChange={(e) => setFilterItemText(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: filterItemText.trim() ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                          backgroundColor: filterItemText.trim() ? "#F0FDF4" : "#fff",
                          fontSize: "0.78rem",
                          boxSizing: "border-box",
                        }}
                      />
                    </th>

                    {/* Filter 3: Serial & Part # */}
                    <th style={{ padding: "6px 14px" }}>
                      <input
                        type="text"
                        placeholder="Filter SN or PN..."
                        value={filterSerialText}
                        onChange={(e) => setFilterSerialText(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: filterSerialText.trim() ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                          backgroundColor: filterSerialText.trim() ? "#F0FDF4" : "#fff",
                          fontSize: "0.78rem",
                          boxSizing: "border-box",
                        }}
                      />
                    </th>

                    {/* Filter 4: Sub-category */}
                    <th style={{ padding: "6px 14px" }}>
                      <select
                        value={filterSubcategory}
                        onChange={(e) => setFilterSubcategory(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: filterSubcategory !== "all" ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                          backgroundColor: filterSubcategory !== "all" ? "#F0FDF4" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "var(--slate-700)",
                        }}
                      >
                        <option value="all">All Sub-categories</option>
                        {allSubcategoriesList.map((subName) => (
                          <option key={subName} value={subName}>
                            {subName}
                          </option>
                        ))}
                      </select>
                    </th>

                    {/* Filter 5: Party (Vendor / Client) */}
                    <th style={{ padding: "6px 14px" }}>
                      <select
                        value={filterParty}
                        onChange={(e) => setFilterParty(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: filterParty !== "all" ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                          backgroundColor: filterParty !== "all" ? "#F0FDF4" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "var(--slate-700)",
                        }}
                      >
                        <option value="all">All Parties</option>
                        {distinctParties.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </th>

                    {/* Filter 6: Logged By Staff */}
                    <th style={{ padding: "6px 14px" }}>
                      <select
                        value={filterLoggedBy}
                        onChange={(e) => setFilterLoggedBy(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: filterLoggedBy !== "all" ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                          backgroundColor: filterLoggedBy !== "all" ? "#F0FDF4" : "#fff",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "var(--slate-700)",
                        }}
                      >
                        <option value="all">All Staff</option>
                        {distinctLoggedBy.map((st) => (
                          <option key={st.name} value={st.name}>
                            {st.label}
                          </option>
                        ))}
                      </select>
                    </th>

                    {/* Filter Action / Reset */}
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={resetAllTableFilters}
                          title="Reset All Column Filters"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: "1px solid var(--danger)",
                            backgroundColor: "var(--danger-bg, #FEF2F2)",
                            color: "var(--danger)",
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          <FiX size={12} /> Clear
                        </button>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {movementLoading ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        Loading purchases and sales for the selected date range...
                      </td>
                    </tr>
                  ) : movements.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        No purchases or sales found for the selected period ({selectedPeriodLabel}).
                      </td>
                    </tr>
                  ) : filteredMovements.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 40, textAlign: "center", color: "var(--slate-500)" }}>
                        No movements match your active column filters.{" "}
                        <button
                          type="button"
                          onClick={resetAllTableFilters}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--primary)",
                            fontWeight: 700,
                            cursor: "pointer",
                            textDecoration: "underline",
                            padding: 0,
                          }}
                        >
                          Click here to clear filters
                        </button>
                      </td>
                    </tr>
                  ) : (
                    paginatedMovements.map((m, idx) => {
                      const isPurchase = m.record_type === "purchase";
                      const transDate = m.transaction_date || "—";

                      return (
                        <tr
                          key={`${m.record_type}-${m.id}-${idx}`}
                          style={{
                            borderBottom: "1px solid var(--slate-100)",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          {/* Date */}
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 700, color: "var(--navy)" }}>
                              {formatDateDisplay(transDate)}
                            </div>
                          </td>

                          {/* Movement Type */}
                          <td style={{ padding: "12px 14px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "3px 9px",
                                borderRadius: 12,
                                fontSize: "0.74rem",
                                fontWeight: 800,
                                backgroundColor: isPurchase ? "#E0F2FE" : "var(--success-bg)",
                                color: isPurchase ? "#0369A1" : "var(--success)",
                              }}
                            >
                              {isPurchase ? <FiArrowDownLeft size={13} /> : <FiArrowUpRight size={13} />}
                              {isPurchase ? "Stock IN (Purchase)" : "Stock OUT (Sale)"}
                            </span>
                          </td>

                          {/* Item & SKU */}
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 700, color: "var(--navy)" }}>{m.item_name}</div>
                            <div style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "var(--slate-500)" }}>
                              SKU: {m.sku}
                            </div>
                          </td>

                          {/* Serial / Part # */}
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "var(--navy)", fontWeight: 600 }}>
                              SN: {m.serial_number || <span style={{ color: "var(--slate-400)" }}>—</span>}
                            </div>
                            <div style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "var(--slate-500)" }}>
                              PN: {m.part_number || <span style={{ color: "var(--slate-400)" }}>—</span>}
                            </div>
                          </td>

                          {/* Sub-category (Category NOT shown) */}
                          <td style={{ padding: "12px 14px" }}>
                            <span
                              style={{
                                padding: "3px 8px",
                                borderRadius: 6,
                                backgroundColor: "var(--slate-100)",
                                color: "var(--slate-700)",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                              }}
                            >
                              {m.subcategory_name || "—"}
                            </span>
                          </td>

                          {/* Party (Vendor / Client) */}
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 700, color: "var(--slate-800)" }}>{m.party_name || "—"}</div>
                            <div style={{ fontSize: "0.74rem", color: "var(--slate-500)" }}>
                              {isPurchase ? "Vendor / Supplier" : "Client / Buyer"}
                            </div>
                          </td>

                          {/* Logged By Staff */}
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 700, color: "var(--slate-800)" }}>
                              {m.created_by_name || "System"}
                            </div>
                            {m.created_by_username && (
                              <div style={{ fontSize: "0.72rem", color: "var(--slate-400)" }}>
                                @{m.created_by_username}
                              </div>
                            )}
                          </td>

                          {/* Inspect Item Lifecycle Action */}
                          <td style={{ padding: "12px 14px", textAlign: "right" }}>
                            <button
                              onClick={() => inspectItemLifecycle(m.serial_number || m.sku || m.item_name)}
                              title="Inspect Full Purchase & Sale History for this Item"
                              style={{
                                padding: "4px 9px",
                                borderRadius: 6,
                                border: "1px solid var(--slate-200)",
                                backgroundColor: "#fff",
                                color: "var(--primary)",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <FiSearch size={11} /> History
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Bar for Movements */}
            {!movementLoading && filteredMovements.length > 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: "1px solid var(--slate-200)",
                  backgroundColor: "var(--slate-50)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  fontSize: "0.84rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing <strong>{moveStartIdx + 1}</strong> to <strong>{moveEndIdx}</strong> of{" "}
                  <strong>{filteredMovements.length}</strong> movements
                  {filteredMovements.length !== movements.length && (
                    <span style={{ color: "var(--slate-400)", marginLeft: 6 }}>
                      (filtered from {movements.length} total)
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <select
                    value={movePageSize}
                    onChange={(e) => setMovePageSize(Number(e.target.value))}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      fontSize: "0.82rem",
                      backgroundColor: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    <option value={10}>10 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                  </select>

                  <button
                    disabled={movePage === 1}
                    onClick={() => setMovePage((p) => Math.max(p - 1, 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: movePage === 1 ? "var(--slate-100)" : "#fff",
                      color: movePage === 1 ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: movePage === 1 ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    <FiChevronLeft size={14} /> Prev
                  </button>

                  <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                    Page {movePage} of {totalMovePages}
                  </span>

                  <button
                    disabled={movePage === totalMovePages}
                    onClick={() => setMovePage((p) => Math.min(p + 1, totalMovePages))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: movePage === totalMovePages ? "var(--slate-100)" : "#fff",
                      color: movePage === totalMovePages ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: movePage === totalMovePages ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    Next <FiChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 2: STAFF ACTIVITY & WORK AUDIT TRAIL                 */}
      {/* ======================================================== */}
      {activeTab === "staff-activity" && (
        <div>
          {/* Staff Breakdown Summary Cards */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)", marginBottom: 12 }}>
              Staff Performance & Logged Actions
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 14,
              }}
            >
              {(userAuditData.users || []).map((u) => {
                const isSelected = auditUserFilter === String(u.id);

                return (
                  <div
                    key={u.id}
                    onClick={() => setAuditUserFilter(isSelected ? "all" : String(u.id))}
                    style={{
                      backgroundColor: isSelected ? "var(--accent-light, #EDE9FE)" : "#fff",
                      borderRadius: "var(--radius-lg)",
                      padding: "16px 18px",
                      border: isSelected ? "2px solid var(--accent, #7C3AED)" : "1px solid var(--slate-200)",
                      boxShadow: isSelected ? "0 4px 12px rgba(124, 58, 237, 0.15)" : "var(--shadow-sm)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.95rem" }}>{u.name}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--slate-500)" }}>
                          @{u.username} • <span style={{ fontWeight: 700, color: "var(--accent)" }}>{u.role}</span>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 10,
                          backgroundColor: u.status === "active" ? "var(--success-bg)" : "var(--danger-bg)",
                          color: u.status === "active" ? "var(--success)" : "var(--danger)",
                        }}
                      >
                        {u.status}
                      </span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                      <div style={{ backgroundColor: "#F8FAFC", padding: "8px 10px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.7rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                          Sales Logged
                        </div>
                        <div style={{ fontWeight: 800, color: "var(--success)", fontSize: "1.15rem", marginTop: 2 }}>
                          {u.total_sales_count}{" "}
                          <span style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 600 }}>
                            sales
                          </span>
                        </div>
                      </div>

                      <div style={{ backgroundColor: "#F8FAFC", padding: "8px 10px", borderRadius: 8 }}>
                        <div style={{ fontSize: "0.7rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                          Purchases Logged
                        </div>
                        <div style={{ fontWeight: 800, color: "#0369A1", fontSize: "1.15rem", marginTop: 2 }}>
                          {u.total_purchases_count}{" "}
                          <span style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 600 }}>
                            purchases
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Audit Filters Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "14px 18px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 18,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            {/* Filter by Staff Member */}
            <select
              value={auditUserFilter}
              onChange={(e) => setAuditUserFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                fontSize: "0.84rem",
                backgroundColor: "#fff",
                fontWeight: 600,
                color: "var(--slate-700)",
              }}
            >
              <option value="all">All Staff Members</option>
              {(userAuditData.users || []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} (@{u.username})
                </option>
              ))}
            </select>

            {/* Filter by Action Type */}
            <select
              value={auditTypeFilter}
              onChange={(e) => setAuditTypeFilter(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                fontSize: "0.84rem",
                backgroundColor: "#fff",
                fontWeight: 600,
                color: "var(--slate-700)",
              }}
            >
              <option value="all">All Actions (Sales & Purchases)</option>
              <option value="sale">Sales Orders Only</option>
              <option value="purchase">Purchase Orders Only</option>
            </select>

            {/* Date Range Inputs */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="date"
                value={auditStartDate}
                onChange={(e) => setAuditStartDate(e.target.value)}
                style={{
                  padding: "7px 10px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.82rem",
                }}
              />
              <span style={{ fontSize: "0.8rem", color: "var(--slate-400)" }}>to</span>
              <input
                type="date"
                value={auditEndDate}
                onChange={(e) => setAuditEndDate(e.target.value)}
                style={{
                  padding: "7px 10px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.82rem",
                }}
              />
            </div>

            {/* Search Input */}
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <FiSearch
                size={16}
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--slate-400)" }}
              />
              <input
                type="text"
                placeholder="Search staff, item, SKU, serial #, client, vendor..."
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 30px 8px 32px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.84rem",
                  boxSizing: "border-box",
                }}
              />
              {auditSearch && (
                <button
                  onClick={() => setAuditSearch("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--slate-400)",
                  }}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            <button
              onClick={fetchUserAuditReport}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                color: "var(--slate-600)",
                fontSize: "0.84rem",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <FiRefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Staff Activity Trail Table: Who made which sale/purchase and when */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600 }}>Timestamp / Date</th>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600 }}>Staff Member</th>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600 }}>Action Performed</th>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600 }}>Item & SKU</th>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600 }}>Sub-category</th>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600 }}>Party (Client / Vendor)</th>
                    <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Inspect</th>
                  </tr>
                </thead>
                <tbody>
                  {userAuditLoading ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        Loading staff audit trail...
                      </td>
                    </tr>
                  ) : auditTransactionsList.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        No transactions logged by staff for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedAuditTransactions.map((t, idx) => {
                      const isSale = t.record_type === "sale";
                      return (
                        <tr
                          key={`audit-${t.id}-${t.record_type}-${idx}`}
                          style={{
                            borderBottom: "1px solid var(--slate-100)",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ fontWeight: 700, color: "var(--navy)" }}>
                              {formatDateDisplay(t.transaction_date)}
                            </div>
                            {t.created_at && (
                              <div style={{ fontSize: "0.74rem", color: "var(--slate-400)", fontWeight: 600 }}>
                                ⏰ {new Date(t.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </div>
                            )}
                          </td>

                          {/* Staff Member */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ fontWeight: 700, color: "var(--navy)" }}>
                              {t.user_name || "Unknown Staff"}
                            </div>
                            <div style={{ fontSize: "0.74rem", color: "var(--slate-500)" }}>
                              @{t.user_username} • <span style={{ fontWeight: 700, color: "var(--accent)" }}>{t.user_role}</span>
                            </div>
                          </td>

                          {/* Action */}
                          <td style={{ padding: "12px 16px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "3px 9px",
                                borderRadius: 12,
                                fontSize: "0.74rem",
                                fontWeight: 800,
                                backgroundColor: isSale ? "var(--success-bg)" : "#E0F2FE",
                                color: isSale ? "var(--success)" : "#0369A1",
                              }}
                            >
                              {isSale ? <FiArrowUpRight size={12} /> : <FiShoppingCart size={12} />}
                              {isSale ? "Logged Sale Order" : "Logged Purchase Order"}
                            </span>
                          </td>

                          {/* Item & SKU */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ fontWeight: 700, color: "var(--slate-800)" }}>{t.item_name}</div>
                            <div style={{ fontSize: "0.74rem", fontFamily: "monospace", color: "var(--slate-500)" }}>
                              SKU: {t.sku}
                            </div>
                          </td>

                          {/* Sub-category (Category NOT shown) */}
                          <td style={{ padding: "12px 16px" }}>
                            <span
                              style={{
                                padding: "3px 8px",
                                borderRadius: 6,
                                backgroundColor: "var(--slate-100)",
                                color: "var(--slate-700)",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                              }}
                            >
                              {t.subcategory_name || "—"}
                            </span>
                          </td>

                          {/* Party */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ fontWeight: 600, color: "var(--slate-800)" }}>{t.party_name || "—"}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--slate-400)" }}>
                              {isSale ? "Customer" : "Supplier"}
                            </div>
                          </td>

                          {/* Action: Inspect */}
                          <td style={{ padding: "12px 16px", textAlign: "right" }}>
                            <button
                              onClick={() => inspectItemLifecycle(t.serial_number || t.sku || t.item_name)}
                              title="Inspect Full History for this Item"
                              style={{
                                padding: "4px 9px",
                                borderRadius: 6,
                                border: "1px solid var(--slate-200)",
                                backgroundColor: "#fff",
                                color: "var(--accent, #7C3AED)",
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <FiSearch size={11} /> History
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Bar for Staff Activity */}
            {!userAuditLoading && auditTransactionsList.length > 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: "1px solid var(--slate-200)",
                  backgroundColor: "var(--slate-50)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  fontSize: "0.84rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing <strong>{auditStartIdx + 1}</strong> to <strong>{auditEndIdx}</strong> of{" "}
                  <strong>{auditTransactionsList.length}</strong> transactions
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <select
                    value={auditPageSize}
                    onChange={(e) => setAuditPageSize(Number(e.target.value))}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      fontSize: "0.82rem",
                      backgroundColor: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    <option value={10}>10 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                  </select>

                  <button
                    disabled={auditPage === 1}
                    onClick={() => setAuditPage((p) => Math.max(p - 1, 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: auditPage === 1 ? "var(--slate-100)" : "#fff",
                      color: auditPage === 1 ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: auditPage === 1 ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    <FiChevronLeft size={14} /> Prev
                  </button>

                  <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                    Page {auditPage} of {totalAuditPages}
                  </span>

                  <button
                    disabled={auditPage === totalAuditPages}
                    onClick={() => setAuditPage((p) => Math.min(p + 1, totalAuditPages))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: auditPage === totalAuditPages ? "var(--slate-100)" : "#fff",
                      color: auditPage === totalAuditPages ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: auditPage === totalAuditPages ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    Next <FiChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: ITEM FULL DETAIL LIFECYCLE (WHEN PURCHASED & SOLD, VENDOR & CLIENT) */}
      {/* ========================================================================= */}
      {activeTab === "item-history" && (
        <div>
          {/* Search Box Card */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "20px 24px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 22,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FiSearch size={18} color="var(--primary)" />
              <span style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)" }}>
                Item Lifecycle & Traceability Search
              </span>
            </div>
            <p style={{ fontSize: "0.84rem", color: "var(--slate-500)", marginBottom: 16 }}>
              Search any item by Name, SKU, Serial Number, or Part Number to view its full history:
              when it was purchased from which vendor, and when it was sold to which client.
            </p>

            <div style={{ position: "relative", maxWidth: 640 }}>
              <FiSearch
                size={18}
                style={{
                  position: "absolute",
                  left: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                }}
              />
              <input
                type="text"
                autoFocus
                placeholder="Type item name, SKU code, serial #, or part #..."
                value={itemSearchTerm}
                onChange={(e) => setItemSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "11px 40px 11px 42px",
                  borderRadius: "var(--radius-md)",
                  border: "1.5px solid var(--primary, #064E3B)",
                  fontSize: "0.92rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {itemSearchTerm && (
                <button
                  onClick={() => {
                    setItemSearchTerm("");
                    setItemHistoryResults([]);
                  }}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--slate-400)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FiX size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Results Display */}
          {itemHistoryLoading ? (
            <div style={{ padding: 48, textAlign: "center", color: "var(--slate-400)", backgroundColor: "#fff", borderRadius: "var(--radius-lg)" }}>
              <FiRefreshCw size={24} className="spin" style={{ marginBottom: 10 }} />
              <div>Fetching complete item lifecycle records...</div>
            </div>
          ) : !itemSearchTerm.trim() ? (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                padding: "48px 24px",
                textAlign: "center",
                border: "1px dashed var(--slate-300)",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "var(--primary-light)",
                  color: "var(--primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 14px",
                }}
              >
                <FiSearch size={26} />
              </div>
              <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)", marginBottom: 6 }}>
                Search an Item to Inspect its Lifecycle
              </div>
              <div style={{ fontSize: "0.85rem", color: "var(--slate-500)", maxWidth: 460, margin: "0 auto" }}>
                Type an item name, SKU, serial number, or part number above. You will see exactly when it was purchased, from which vendor, when it was sold, and to which client.
              </div>
            </div>
          ) : itemHistoryResults.length === 0 ? (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "var(--radius-lg)",
                padding: "40px 24px",
                textAlign: "center",
                border: "1px solid var(--slate-200)",
              }}
            >
              <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "1rem", marginBottom: 4 }}>
                No matching items found for "{itemSearchTerm}"
              </div>
              <div style={{ fontSize: "0.84rem", color: "var(--slate-500)" }}>
                Try searching with a different SKU code, serial number, or item name.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {itemHistoryResults.map((item) => {
                const hasPurchases = item.purchases && item.purchases.length > 0;
                const hasSales = item.sales && item.sales.length > 0;
                const isSold = Boolean(item.is_sale) || hasSales;

                return (
                  <div
                    key={item.id}
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "var(--radius-lg)",
                      border: "1px solid var(--slate-200)",
                      boxShadow: "var(--shadow-sm)",
                      overflow: "hidden",
                    }}
                  >
                    {/* Item Header Strip */}
                    <div
                      style={{
                        padding: "16px 20px",
                        backgroundColor: "var(--slate-50)",
                        borderBottom: "1px solid var(--slate-200)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)" }}>
                            {item.item_name}
                          </span>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: "0.78rem",
                              backgroundColor: "var(--slate-200)",
                              color: "var(--navy)",
                              padding: "2px 8px",
                              borderRadius: 4,
                              fontWeight: 700,
                            }}
                          >
                            SKU: {item.sku || "—"}
                          </span>
                          {item.subcategory_name && (
                            <span
                              style={{
                                fontSize: "0.76rem",
                                backgroundColor: "var(--primary-light)",
                                color: "var(--primary)",
                                padding: "2px 8px",
                                borderRadius: 10,
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <FiTag size={11} /> {item.subcategory_name}
                            </span>
                          )}
                        </div>

                        <div style={{ display: "flex", gap: 14, fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 5, flexWrap: "wrap" }}>
                          <span>SN: <code style={{ color: "var(--navy)", fontWeight: 700 }}>{item.serial_number || "—"}</code></span>
                          <span>PN: <code style={{ color: "var(--navy)", fontWeight: 700 }}>{item.part_number || "—"}</code></span>
                          {item.warranty_end_date && <span>Warranty End: <strong>{new Date(item.warranty_end_date).toLocaleDateString()}</strong></span>}
                        </div>
                      </div>

                      {/* Current Status Badge */}
                      <div>
                        {isSold ? (
                          <span
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: 800,
                              backgroundColor: "#FEF3C7",
                              color: "#B45309",
                              padding: "5px 12px",
                              borderRadius: 14,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <FiCheckCircle size={14} /> Sold (Delivered to Client)
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: 800,
                              backgroundColor: "var(--success-bg)",
                              color: "var(--success)",
                              padding: "5px 12px",
                              borderRadius: 14,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <FiBox size={14} /> In Stock ({item.current_stock || 1} units available)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Timeline Body: When Purchased from Vendor & When Sold to Client */}
                    <div
                      style={{
                        padding: "20px",
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                        gap: 20,
                      }}
                    >
                      {/* Left: Purchase / Procurement History */}
                      <div
                        style={{
                          backgroundColor: "#F8FAFC",
                          borderRadius: "var(--radius-md)",
                          padding: "16px 18px",
                          border: "1px solid var(--slate-200)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              backgroundColor: "#E0F2FE",
                              color: "#0369A1",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FiArrowDownLeft size={16} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.92rem" }}>
                              1. Procurement & Vendor Purchase
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "var(--slate-500)" }}>
                              When it was purchased & from which vendor
                            </div>
                          </div>
                        </div>

                        {hasPurchases ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {item.purchases.map((p) => (
                              <div
                                key={p.id}
                                style={{
                                  backgroundColor: "#fff",
                                  borderRadius: 8,
                                  padding: "12px 14px",
                                  border: "1px solid var(--slate-200)",
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                  <div>
                                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                                      Purchased From Vendor
                                    </div>
                                    <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--navy)" }}>
                                      🏢 {p.vendor_name || "Supplier / Vendor"}
                                    </div>
                                    {p.vendor_contact && (
                                      <div style={{ fontSize: "0.75rem", color: "var(--slate-500)" }}>
                                        Contact: {p.vendor_contact}
                                      </div>
                                    )}
                                  </div>

                                  <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                                      Purchase Date
                                    </div>
                                    <div style={{ fontSize: "0.88rem", fontWeight: 800, color: "var(--navy)" }}>
                                      📅 {formatDateDisplay(p.purchase_date)}
                                    </div>
                                  </div>
                                </div>

                                {p.logged_by_name && (
                                  <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px dashed var(--slate-200)", fontSize: "0.74rem", color: "var(--slate-500)" }}>
                                    Logged by staff: <strong>{p.logged_by_name}</strong> (@{p.logged_by_username})
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ padding: "16px", textAlign: "center", color: "var(--slate-400)", fontSize: "0.84rem", backgroundColor: "#fff", borderRadius: 8 }}>
                            Direct inventory catalog entry (no vendor purchase order logged).
                          </div>
                        )}
                      </div>

                      {/* Right: Sale / Customer Delivery History */}
                      <div
                        style={{
                          backgroundColor: "#F8FAFC",
                          borderRadius: "var(--radius-md)",
                          padding: "16px 18px",
                          border: "1px solid var(--slate-200)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              backgroundColor: "var(--success-bg)",
                              color: "var(--success)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FiArrowUpRight size={16} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.92rem" }}>
                              2. Sale & Client Delivery
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "var(--slate-500)" }}>
                              When it was sold & to which client
                            </div>
                          </div>
                        </div>

                        {hasSales ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {item.sales.map((s) => (
                              <div
                                key={s.id}
                                style={{
                                  backgroundColor: "#fff",
                                  borderRadius: 8,
                                  padding: "12px 14px",
                                  border: "1px solid var(--slate-200)",
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                  <div>
                                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                                      Sold To Client
                                    </div>
                                    <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--navy)" }}>
                                      🤝 {s.client_name || "Customer / Client"}
                                    </div>
                                    {s.client_contact && (
                                      <div style={{ fontSize: "0.75rem", color: "var(--slate-500)" }}>
                                        Contact: {s.client_contact}
                                      </div>
                                    )}
                                  </div>

                                  <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: "0.72rem", color: "var(--slate-400)", fontWeight: 700, textTransform: "uppercase" }}>
                                      Sale Date
                                    </div>
                                    <div style={{ fontSize: "0.88rem", fontWeight: 800, color: "var(--success)" }}>
                                      📅 {formatDateDisplay(s.sale_date)}
                                    </div>
                                  </div>
                                </div>

                                {s.logged_by_name && (
                                  <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px dashed var(--slate-200)", fontSize: "0.74rem", color: "var(--slate-500)" }}>
                                    Logged by staff: <strong>{s.logged_by_name}</strong> (@{s.logged_by_username})
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: "24px 16px",
                              textAlign: "center",
                              color: "var(--success)",
                              fontSize: "0.86rem",
                              backgroundColor: "#fff",
                              borderRadius: 8,
                              border: "1px dashed var(--success)",
                            }}
                          >
                            <FiCheckCircle size={22} style={{ marginBottom: 6 }} />
                            <div style={{ fontWeight: 800 }}>Currently In Stock</div>
                            <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 2 }}>
                              This item has not yet been sold to any client and is available on-hand.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
